import { ParameterData } from '@/components/ui/ParameterGroupCard';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { WeibullAnalysisMetadata } from '@/services/api/analysisApi';

/**
 * Builds the "Model Fit" card's parameter rows purely from backend-computed values.
 *
 * The goodness-of-fit metrics (R², adjusted R², RMSE) are now measured server-side in linearized
 * Weibull-probability space and returned on `parameters`. This helper deliberately performs NO
 * client-side statistics: it only reads and formats the backend fields. (Previously the page
 * synthesised Adjusted R² via an inline n/k formula and RMSE via `sqrt(1-R²)*0.5`, which contradicted
 * the probability plot on heavily-censored fits.)
 *
 * Sample Size distinguishes the failures actually used in the regression from the total record count
 * (which includes suspensions), so "N points" no longer misleadingly shows the full population.
 */
export function buildModelFitParameters(
  analysis: WeibullAnalysisResult,
  metadata: WeibullAnalysisMetadata | null,
  selectedMethod: string
): ParameterData[] {
  const failuresUsed = analysis.summary.sampleSize ?? 0;
  const totalRecords = metadata?.totalSamples ?? failuresUsed;

  return [
    {
      label: 'Goodness of Fit (R²)',
      value: analysis.parameters.rSquared || 0,
    },
    {
      label: 'Adjusted R²',
      value: analysis.parameters.adjustedRSquared ?? 0,
      skipInterpretation: true,
    },
    {
      label: 'RMSE',
      value: analysis.parameters.rmse ?? 0,
      skipInterpretation: true,
    },
    {
      label: 'Sample Size',
      value: `${failuresUsed} failures of ${totalRecords} records`,
      skipInterpretation: true,
    },
    {
      label: 'Method',
      value: selectedMethod.toUpperCase(),
      skipInterpretation: true,
    },
  ];
}
